if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.captureVariantScrollPosition = (event) => {
          if (!event.target.closest('variant-selects')) return;
          this.variantInteractionScrollPosition = { left: window.scrollX, top: window.scrollY };
        };
        this.addEventListener('pointerdown', this.captureVariantScrollPosition, { passive: true });

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.stylePaymentTerms();
        this.styleReviewSeparator();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      styleReviewSeparator() {
        const applySeparator = () => {
          this.querySelectorAll('.product-review-summary').forEach((summary) => {
            const rating = summary.querySelector('.product-review-summary__rating');
            if (rating && rating.dataset.separatorStyledCc !== 'true') {
              const ratingText = rating.textContent.replace(/\s*\|\s*$/, '').trim();

              if (ratingText) {
                rating.textContent = ratingText;
                rating.dataset.separatorStyledCc = 'true';

                const separator = document.createElement('span');
                separator.className = 'product-review-summary__separator';
                separator.setAttribute('aria-hidden', 'true');
                separator.textContent = '|';
                rating.after(separator);
              }
            }

            if (summary.dataset.rowClickableCc !== 'true') {
              summary.dataset.rowClickableCc = 'true';
              summary.addEventListener(
                'click',
                (event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  const reviewTargets = [
                    '#looxReviews [data-loox-component="review-widget"]',
                    '.loox-reviews-default',
                    '[data-loox-component="review-widget"]',
                    '#loox-reviews',
                    '#shopify-product-reviews',
                    '#looxReviews',
                  ];
                  const reviewSection = reviewTargets
                    .map((selector) => document.querySelector(selector))
                    .find((element) => element && element.getBoundingClientRect().height > 0);

                  reviewSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                },
                true
              );
            }
          });
        };

        customElements.whenDefined('product-review-summary').then(() => {
          applySeparator();

          if (this.reviewSeparatorObserverCc) return;
          this.reviewSeparatorObserverCc = new MutationObserver(applySeparator);
          this.reviewSeparatorObserverCc.observe(this, { childList: true, subtree: true });
        });
      }

      stylePaymentTerms() {
        customElements.whenDefined('shopify-payment-terms').then(() => {
          this.querySelectorAll('shopify-payment-terms').forEach((paymentTerms) => {
            const shadowRoot = paymentTerms.shadowRoot;
            if (!shadowRoot) return;

            if (!shadowRoot.querySelector('[data-theme-payment-terms-cc]')) {
              const style = document.createElement('style');
              style.dataset.themePaymentTermsCc = '';
              style.textContent = `
                #shopify-installments {
                  font-family: var(--font-body-family) !important;
                  font-size: 1.2rem !important;
                  font-style: var(--font-body-style) !important;
                  font-weight: var(--font-body-weight) !important;
                  letter-spacing: inherit !important;
                  line-height: inherit !important;
                }

                #shopify-installments p {
                  align-items: center;
                  display: flex;
                  flex-wrap: wrap;
                  gap: 0;
                  margin: 0;
                }

                #shopify-installments-content {
                  align-items: center;
                  display: inline-flex;
                  flex-wrap: wrap;
                  gap: 0.3rem;
                }

                #shopify-installments-content [data-payment-amount-cc] {
                  color: rgb(var(--color-foreground));
                  font-weight: 600;
                }

                #shopify-installments-content [data-payment-per-month-cc] {
                  display: inline-flex;
                  align-items: baseline;
                  white-space: nowrap;
                }

                #shopify-installments-content [data-shop-pay-logo-cc] {
                  color: #5a31f4;
                  display: inline-block;
                  width: auto;
                  height: 1.2rem;
                }

                #shopify-installments-cta {
                  align-items: center;
                  display: inline-flex;
                  padding: 0 !important;
                }

                #shopify-installments-cta svg {
                  height: 1.2rem;
                  width: 1.2rem;
                }
              `;
              shadowRoot.appendChild(style);
            }

            const formatPaymentTerms = () => {
              const content = shadowRoot.querySelector('#shopify-installments-content');
              if (!content || content.querySelector('[data-payment-amount-cc]')) return;

              const nativeShopPayLogo = content.querySelector('svg');
              const meta = JSON.parse(paymentTerms.getAttribute('shopify-meta') || '{}');
              const variantId = Number(paymentTerms.getAttribute('variant-id'));
              const variant = meta.variants?.find((item) => item.id === variantId);
              if (!nativeShopPayLogo || !variant?.price_per_term || !variant?.number_of_payment_terms) return;

              const shopPayLogo = nativeShopPayLogo.cloneNode(true);
              shopPayLogo.dataset.shopPayLogoCc = '';
              shopPayLogo.setAttribute('aria-label', 'Shop Pay');

              const amount = document.createElement('strong');
              amount.dataset.paymentAmountCc = '';
              amount.textContent = variant.price_per_term;

              const paymentPerMonth = document.createElement('span');
              paymentPerMonth.dataset.paymentPerMonthCc = '';
              paymentPerMonth.append(amount, document.createTextNode('/mo'));

              const introduction = document.createElement('span');
              introduction.textContent = `${variant.number_of_payment_terms} interest-free payments of`;

              const withLabel = document.createElement('span');
              withLabel.textContent = 'with';

              content.replaceChildren(
                introduction,
                paymentPerMonth,
                withLabel,
                shopPayLogo
              );

              const ctaIcon = shadowRoot.querySelector('#shopify-installments-cta svg');
              if (ctaIcon && !ctaIcon.dataset.infoIconCc) {
                ctaIcon.dataset.infoIconCc = '';
                ctaIcon.setAttribute('viewBox', '0 0 20 20');
                ctaIcon.innerHTML =
                  '<circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M10 8.7v4.6M10 6.6h.01" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>';
              }
            };

            formatPaymentTerms();

            if (!paymentTerms.paymentTermsObserverCc) {
              paymentTerms.paymentTermsObserverCc = new MutationObserver(formatPaymentTerms);
              paymentTerms.paymentTermsObserverCc.observe(shadowRoot, { childList: true, subtree: true });
            }
          });
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.removeEventListener('pointerdown', this.captureVariantScrollPosition);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        if (this.variantInteractionScrollPosition) {
          window.scrollTo({ ...this.variantInteractionScrollPosition, behavior: 'instant' });
        }

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        clearTimeout(this.variantUpdateVisualTimeout);
        this.classList.add('is-updating-variant');
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        const requestController = new AbortController();
        this.abortController = requestController;
        const scrollPosition = this.variantInteractionScrollPosition || { left: window.scrollX, top: window.scrollY };
        this.variantInteractionScrollPosition = null;

        fetch(requestUrl, { signal: requestController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const previousOverflowAnchor = document.documentElement.style.overflowAnchor;
            document.documentElement.style.overflowAnchor = 'none';
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
            window.scrollTo({ ...scrollPosition, behavior: 'instant' });
            requestAnimationFrame(() => {
              window.scrollTo({ ...scrollPosition, behavior: 'instant' });
              document.documentElement.style.overflowAnchor = previousOverflowAnchor;
            });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          })
          .finally(() => {
            if (this.abortController !== requestController) return;

            this.variantUpdateVisualTimeout = setTimeout(() => {
              if (this.abortController === requestController) this.classList.remove('is-updating-variant');
            }, 220);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          false
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
